// The ONE place that turns a scorer `app_state` into games, points and a
// games-won tally. Rules are lifted verbatim from the save chain
// (matchModel.transformAppStateToMatchPoints / calculateMatchSummary) so that
// a live surface shows exactly what finalize will persist. Ported to SQL as
// public.score_resolve (supabase/migrations/20260912110000_score_resolver.sql)
// and to Dart (playpulse-app lib/features/matches/domain/score_resolver.dart);
// all three are pinned by test/fixtures/scorecard/*.json. Change a rule here →
// regenerate the fixtures → change the other two ports in the same commit.
//
// Spec §3 — it lives in the playpulse-app repo, not this one:
// playpulse-app/docs/superpowers/specs/2026-09-12-unified-score-resolver-design.md
import type { ReconstructedGame, ScorecardPoint } from './timeline';

export type Side = 'player_1' | 'player_2';

export interface ResolvedPoint {
  id: string;
  order_number: number;
  player_1_score: number;
  player_2_score: number;
  /** The server BEFORE this action. */
  serving_player: Side;
  serving_side: string;
  point_type: string;
  metadata: unknown;
  played_at: string;
}

export interface ResolvedGame {
  game_number: number;
  player_1_score: number;
  player_2_score: number;
  completed: boolean;
  duration_seconds: number | null;
  points: ResolvedPoint[];
}

export interface ResolvedMatch {
  /** Completed games in order, then the current game (if any). */
  games: ResolvedGame[];
  player_1_games: number;
  player_2_games: number;
  current_game: ResolvedGame | null;
  is_complete: boolean;
}

export interface ReplayResult {
  player_1_score: number;
  player_2_score: number;
  points: ResolvedPoint[];
}

export class InvalidActionTimestampError extends Error {}

/**
 * Rule 6 — the accepted action-timestamp window: integer epoch milliseconds
 * inside year 1 .. year 9999. Identical to the SQL port's guard
 * (score_replay_game in 20260912110000_score_resolver.sql) and the Dart port's,
 * so all three accept and reject exactly the same values.
 *
 * Why TypeScript needs a bound at all, when `new Date(ms)` happily represents
 * ±8.64e15: this module is not only a reader, it is the WRITER — `played_at`
 * goes into two `timestamptz` columns. One millisecond outside this window the
 * literal TypeScript produces is one Postgres refuses outright, so no widening
 * of a column or a bound can rescue it:
 *   new Date(253402300800000).toISOString()  -> '+010000-01-01T00:00:00.000Z'
 *     => ERROR: time zone displacement out of range
 *   new Date(-62135596800001).toISOString()  -> '0000-12-31T23:59:59.999Z'
 *     => ERROR: date/time field value out of range
 * `saveMatchAppState` has no transaction: it writes app_state → delete
 * games+points → insert games → insert points → completion fields, so a
 * rejection at the point insert leaves the match `in_progress` with its
 * app_state saved and every derived row rebuildable from it. Before that
 * ordering the completion flag was written FIRST, and the same rejection left
 * the match marked completed with ZERO points and the previous points already
 * deleted. Rejecting here — before the first write — is what makes a corrupt
 * log fail the save instead of half-applying it.
 */
export const MIN_ACTION_TIMESTAMP_MS = -62135596800000; // 0001-01-01T00:00:00.000Z
export const MAX_ACTION_TIMESTAMP_MS = 253402300799999; // 9999-12-31T23:59:59.999Z

export class InvalidActionIdError extends Error {}

/**
 * Rule 7 — an action's `id` must be a JSON string. Absent, JSON null, number,
 * boolean, object and array are all invalid, and the whole resolve is refused:
 * strict throws, `tryResolveAppState` returns null, and the SQL port's
 * score_replay_game returns NULL (so its callers yield a null live score rather
 * than raising). Exactly rule 6's strict/lenient shape.
 *
 * Both producers already declare the id as a required non-nullable string —
 * web `GameAction.id: string`, minted by `createGameAction` from
 * `Date.now().toString() + Math.random()...`, and the Dart scorer's
 * `required String id` — so nothing legitimate carries another type. What the
 * rule buys is port agreement: a 1267-case differential fuzz of this module
 * against the SQL port left 15 divergences and every one of them was an `id`,
 * in two classes that the rule collapses rather than patches.
 *   * Rendering. This port coerced (`String(action.id ?? '')`) and SQL used
 *     `coalesce(v_a ->> 'id', '')`, so an object id rendered '[object Object]'
 *     here and '{}' there.
 *   * Comparison. Rule 4's between-games check compared raw values here and
 *     TEXT in SQL (`->> 'id'`), so a JSON number 5 and the string "5" were
 *     different here and equal there — one port skipping the current game the
 *     other replayed.
 * With one possible type left there is nothing to coerce and nothing to
 * compare across types, so both ports now read the id strictly and emit it
 * verbatim. The rule is enforced wherever an id is read — the replay AND rule
 * 4's comparison (which reads ids the replay has not necessarily validated:
 * `allGameActions` entries past `matchState.games` are never replayed).
 */
function actionId(action: Record<string, unknown>): string {
  const id = action.id;
  if (typeof id !== 'string') {
    throw new InvalidActionIdError(
      `action id must be a string, got ${id === null ? 'null' : Array.isArray(id) ? 'array' : typeof id}`,
    );
  }
  return id;
}

const isObj = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);
const asArray = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const otherPlayer = (p: unknown) => (p === 'player1' ? 'player2' : 'player1');

/**
 * Rules 3, 6 and 7. Replays one game's action log. The server starts at
 * `initialServingPlayer` and flips whenever the scorer is not the server
 * (a scorer of `undefined` — a point with no playerId — also flips; kept
 * from the save chain). Every action, including let_call/conduct, emits a row.
 */
export function replayGame(actions: unknown[], initialServingPlayer: string): ReplayResult {
  let p1 = 0;
  let p2 = 0;
  let server = initialServingPlayer;
  const points: ResolvedPoint[] = [];

  actions.forEach((raw, index) => {
    const action = isObj(raw) ? raw : {};
    const md = isObj(action.metadata) ? action.metadata : undefined;
    const servingBefore: Side = server === 'player2' ? 'player_2' : 'player_1';

    if (action.type === 'point') {
      // `=== true`, NOT truthiness: the SQL port tests
      // `v_md -> 'isStroke' = 'true'::jsonb` and the Dart port `== true`, so
      // only a literal boolean is a decision in those two. Under truthiness
      // metadata {isNoLet: 1} credited player 2 here while SQL credited
      // player 1 — the point silently awarded to the WRONG player rather than
      // the input being rejected. Safe to tighten: both producers
      // (SquashScoreApp's `no` and `stroke` handlers, via createGameAction)
      // write literal `true`, and GameAction declares `boolean | undefined`.
      const scorer =
        md?.isStroke === true
          ? action.playerId
          : md?.isNoLet === true
            ? otherPlayer(action.playerId)
            : action.playerId;
      if (scorer === 'player1') p1++;
      else if (scorer === 'player2') p2++;
      if (scorer !== server) server = server === 'player1' ? 'player2' : 'player1';
    }

    // Rule 6 — integer epoch milliseconds inside year 1 .. 9999, the same
    // window the SQL and Dart ports accept. The save chain's original
    // `new Date(action.timestamp)` accepted far more than a `timestamptz`
    // column can store; see MIN/MAX_ACTION_TIMESTAMP_MS above.
    const ts = action.timestamp;
    if (
      typeof ts !== 'number' ||
      !Number.isInteger(ts) ||
      ts < MIN_ACTION_TIMESTAMP_MS ||
      ts > MAX_ACTION_TIMESTAMP_MS
    ) {
      throw new InvalidActionTimestampError(`action ${String(action.id)} has an invalid timestamp`);
    }
    const date = new Date(ts);

    // Rule 7 — a JSON string id or nothing; see actionId above. Emitted
    // verbatim, like the SQL port's `v_a ->> 'id'`: with the type pinned there
    // is no coercion left for the two ports to disagree about.
    const id = actionId(action);

    points.push({
      id,
      order_number: index + 1,
      player_1_score: p1,
      player_2_score: p2,
      serving_player: servingBefore,
      serving_side:
        typeof md?.serviceBoxSide === 'string' && md.serviceBoxSide ? md.serviceBoxSide : 'right',
      point_type: typeof action.type === 'string' && action.type ? action.type : 'point',
      metadata: action.metadata ?? null,
      played_at: date.toISOString(),
    });
  });

  return { player_1_score: p1, player_2_score: p2, points };
}

/**
 * Rules 1, 2, 4, 5. Strict: throws InvalidActionTimestampError (rule 6) or
 * InvalidActionIdError (rule 7) — the save chain must refuse to persist a
 * corrupt log. Returns null when the blob has no usable matchState.
 */
export function resolveAppState(appState: unknown): ResolvedMatch | null {
  if (!isObj(appState) || !isObj(appState.matchState)) return null;
  const ms = appState.matchState;
  const completedGames = asArray(ms.games);
  const allGameActions = asArray(appState.allGameActions);
  const currentActions = asArray(appState.gameActions);
  const initial =
    typeof ms.initialServingPlayer === 'string' && ms.initialServingPlayer
      ? ms.initialServingPlayer
      : 'player1';

  // Rule 2: one resolved game per matchState.games entry; score from the
  // entry, points from replaying the matching action log.
  const games: ResolvedGame[] = completedGames.map((raw, i) => {
    const g = isObj(raw) ? raw : {};
    const replay = replayGame(asArray(allGameActions[i]), initial);
    return {
      game_number: i + 1,
      player_1_score: typeof g.player1 === 'number' ? g.player1 : 0,
      player_2_score: typeof g.player2 === 'number' ? g.player2 : 0,
      completed: true,
      duration_seconds: typeof g.duration === 'number' ? g.duration : null,
      points: replay.points,
    };
  });

  // Rule 5.
  let p1Games = 0;
  let p2Games = 0;
  for (const g of games) {
    if (g.player_1_score > g.player_2_score) p1Games++;
    else if (g.player_2_score > g.player_1_score) p2Games++;
  }

  // Rule 4.
  let current: ResolvedGame | null = null;
  if (currentActions.length > 0) {
    const last = allGameActions[allGameActions.length - 1];
    // Rule 7 applies here too: these ids have not necessarily been through the
    // replay (an `allGameActions` entry past `matchState.games` is never
    // replayed), and a non-string id is what made this comparison disagree with
    // the SQL port's text comparison. Both ports now refuse instead of
    // comparing across types, so `===` on two strings is the whole rule.
    const idOf = (a: unknown) => actionId(isObj(a) ? a : {});
    const isDuplicate =
      Array.isArray(last) &&
      last.length === currentActions.length &&
      last.every((a, i) => idOf(a) === idOf(currentActions[i]));
    if (!isDuplicate) {
      const replay = replayGame(currentActions, initial);
      const n =
        typeof ms.currentGame === 'number' && ms.currentGame > 0
          ? ms.currentGame
          : allGameActions.length + 1;
      current = {
        game_number: n,
        player_1_score: replay.player_1_score,
        player_2_score: replay.player_2_score,
        completed: false,
        duration_seconds: null,
        points: replay.points,
      };
      games.push(current);
    }
  }

  return {
    games,
    player_1_games: p1Games,
    player_2_games: p2Games,
    current_game: current,
    is_complete: ms.isMatchComplete === true,
  };
}

/**
 * Lenient entry point for readers: any throw becomes null — an invalid action
 * timestamp (rule 6) or a non-string action id (rule 7) costs the live score,
 * never the surrounding payload. The SQL port's NULL return is the same
 * contract.
 */
export function tryResolveAppState(appState: unknown): ResolvedMatch | null {
  try {
    return resolveAppState(appState);
  } catch {
    return null;
  }
}

/** Resolved games in the `match_game` + `match_game_point` row shape the scorecard renders. */
export function toScorecardGames(resolved: ResolvedMatch): ReconstructedGame[] {
  return resolved.games.map((g) => ({
    id: `live-game-${g.game_number}`,
    game_number: g.game_number,
    player_1_score: g.player_1_score,
    player_2_score: g.player_2_score,
    match_game_point: g.points.map(
      (p): ScorecardPoint => ({
        id: p.id,
        order_number: p.order_number,
        player_1_score: p.player_1_score,
        player_2_score: p.player_2_score,
        serving_player: p.serving_player,
        point_type: p.point_type,
        created_at: p.played_at,
        metadata: p.metadata,
      }),
    ),
  }));
}
